import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Plus, RefreshCw } from 'lucide-react';
import { COMPLAINT_PRIORITIES, COMPLAINT_STATUSES, labelFor, staffLabel, receptionApiErrorMessage } from './receptionConstants';
import { useReceptionStaff } from './useReceptionData';

const empty = () => ({
  complainant_name: '', complaint_type: '', description: '', priority: 'medium', assigned_user_id: '',
});

export default function ReceptionComplaints() {
  const { toast } = useToast();
  const { canManageFeature, hasPermission } = useAuth();
  const canManage = canManageFeature('reception') || hasPermission('manage_reception');
  const canDelete = hasPermission('delete_reception');
  const staff = useReceptionStaff();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editRow, setEditRow] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reception/complaints');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const save = async () => {
    if (!form.complainant_name?.trim()) {
      toast({ title: 'Complainant name required', variant: 'destructive' });
      return;
    }
    try {
      if (editRow) {
        await api.put(`/reception/complaints/${editRow.id}`, form);
        toast({ title: 'Complaint updated' });
      } else {
        await api.post('/reception/complaints', form);
        toast({ title: 'Complaint registered' });
      }
      setOpen(false);
      setEditRow(null);
      setForm(empty());
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      complainant_name: row.complainant_name || '',
      complaint_type: row.complaint_type || '',
      description: row.description || '',
      priority: row.priority || 'medium',
      assigned_user_id: row.assigned_user_id ? String(row.assigned_user_id) : '',
      status: row.status || 'open',
      resolution_notes: row.resolution_notes || '',
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete complaint?')) return;
    try {
      await api.delete(`/reception/complaints/${id}`);
      toast({ title: 'Deleted' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h1 className="text-2xl font-bold">Complaints</h1><p className="text-muted-foreground">Register and track complaints</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchItems}><RefreshCw className="w-4 h-4" /></Button>
          {canManage && <Button onClick={() => { setEditRow(null); setForm(empty()); setOpen(true); }}><Plus className="w-4 h-4 mr-2" />New Complaint</Button>}
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle>Complaint Register</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Loading…</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground">
                <th className="py-2">#</th><th className="py-2">Complainant</th><th className="py-2">Type</th>
                <th className="py-2">Priority</th><th className="py-2">Status</th><th className="py-2">Assigned</th><th className="py-2">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-2 font-mono text-xs">{r.complaint_number}</td>
                    <td className="py-2">{r.complainant_name}</td>
                    <td className="py-2">{r.complaint_type || '—'}</td>
                    <td className="py-2">{labelFor(COMPLAINT_PRIORITIES, r.priority)}</td>
                    <td className="py-2">{labelFor(COMPLAINT_STATUSES, r.status)}</td>
                    <td className="py-2">{staffLabel({ first_name: r.assigned_first_name, last_name: r.assigned_last_name })}</td>
                    <td className="py-2 flex gap-2">
                      {canManage && <Button size="sm" variant="outline" onClick={() => openEdit(r)}>Edit</Button>}
                      {canDelete && <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>Delete</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editRow ? 'Update Complaint' : 'Register Complaint'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Complainant Name *</Label><Input value={form.complainant_name} onChange={(e) => setForm({ ...form, complainant_name: e.target.value })} /></div>
            <div><Label>Type</Label><Input value={form.complaint_type} onChange={(e) => setForm({ ...form, complaint_type: e.target.value })} /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COMPLAINT_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editRow && (
              <>
                <div><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{COMPLAINT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Resolution Notes</Label><Input value={form.resolution_notes || ''} onChange={(e) => setForm({ ...form, resolution_notes: e.target.value })} /></div>
              </>
            )}
            <div><Label>Assign To</Label>
              <Select value={form.assigned_user_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_user_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={String(s.id)}>{staffLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
